import { useEffect, useState } from "react";
import { X, Share, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isNative } from "@/lib/platform";
import ffaLogo from "@/assets/ffa-logo.png";

const DISMISS_KEY = "ffa_install_dismissed";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  const blocked = isNative || isStandalone() || recentlyDismissed();

  useEffect(() => {
    if (blocked) return;

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), 2500);
    };
    const onInstalled = () => {
      setVisible(false);
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    let t: ReturnType<typeof setTimeout> | undefined;
    if (isIOS) {
      setIosMode(true);
      t = setTimeout(() => setVisible(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, [blocked]);

  if (blocked || !visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[150] safe-bottom">
      <div className="mx-auto max-w-2xl bg-card border-t border-border/50 shadow-lg p-3 sm:rounded-t-xl">
        <div className="flex items-center gap-3">
          <img src={ffaLogo} alt="" className="w-10 h-10 rounded-lg object-contain shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">
              {iosMode && !deferred ? "Install this app" : "Install Fantasy Football Africa"}
            </p>
            <p className="text-xs text-muted-foreground leading-snug">
              {iosMode && !deferred ? (
                <span className="inline-flex items-center gap-1">
                  Tap the <Share className="inline h-3 w-3" /> Share button, then "Add to Home Screen".
                </span>
              ) : (
                "Add it to your home screen for a faster, app-like experience."
              )}
            </p>
          </div>
          {deferred && (
            <Button size="sm" onClick={install} className="shrink-0">
              <Download className="h-4 w-4 mr-1" />
              Install
            </Button>
          )}
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="shrink-0 p-2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
